import { useLocalization } from '@ais-ui/locale-management';
import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
} from '@ais-ui/ui-core';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { EventsFormValues } from './useEventsForm';

import { cn } from '@ais-ui/utils';
import { useNotificationSound } from '../../utils/useNotificationSound';

interface EventsFormProps {
  form: UseFormReturn<EventsFormValues>;
}

export const EventsForm = ({ form }: EventsFormProps) => {
  const { messages } = useLocalization();
  const { isPlaying, playTestSound } = useNotificationSound();

  return (
    <Form {...form}>
      <div className="space-y-4">
        <FormField
          key="lookback_time_in_minutes"
          control={form.control}
          name="lookback_time_in_minutes"
          render={({ field }) => (
            <FormItem
              className={cn(
                'flex flex-row items-center justify-between border rounded-lg p-4'
              )}
            >
              <div className="space-y-0.5">
                <FormLabel className="text-base">
                  {messages.settingsEventsLookbackLabel}
                </FormLabel>
                <FormDescription>
                  {messages.settingsEventsLookbackDescription}
                </FormDescription>
              </div>
              <FormControl
                id="settings-events-lookback"
                onChange={() => form.clearErrors()}
              >
                <Input
                  className="w-20"
                  type="number"
                  {...field}
                  onChange={(e) => {
                    if (!e.target.value) {
                      field.onChange('');
                    } else {
                      // Convert the string value to a number
                      const numberValue = parseFloat(e.target.value);
                      field.onChange(numberValue);
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Sound Alert Levels - Always visible */}
        <div className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">
                {messages.settingsEventsSoundAlertLevelsLabel}
              </Label>
              <p className="text-sm text-muted-foreground">
                {messages.settingsEventsSoundAlertLevelsDescription}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={playTestSound}
              disabled={isPlaying}
              className="whitespace-nowrap"
            >
              {isPlaying ? 'Playing...' : 'Test Sound'}
            </Button>
          </div>

          <div className="space-y-3">
            {/* Note Level */}
            <FormField
              key="soundAlertLevels.note"
              control={form.control}
              name="soundAlertLevels.note"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-3 space-y-0">
                  <FormControl>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="sound-alert-note"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                      <label
                        htmlFor="sound-alert-note"
                        className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {messages.settingsEventsSoundAlertNoteLabel}
                      </label>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Warning Level */}
            <FormField
              key="soundAlertLevels.warning"
              control={form.control}
              name="soundAlertLevels.warning"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-3 space-y-0">
                  <FormControl>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="sound-alert-warning"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                      <label
                        htmlFor="sound-alert-warning"
                        className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {messages.settingsEventsSoundAlertWarningLabel}
                      </label>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Alert Level */}
            <FormField
              key="soundAlertLevels.alert"
              control={form.control}
              name="soundAlertLevels.alert"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-3 space-y-0">
                  <FormControl>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="sound-alert-alert"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                      <label
                        htmlFor="sound-alert-alert"
                        className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {messages.settingsEventsSoundAlertAlertLabel}
                      </label>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>
    </Form>
  );
};
